import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faTag,
  faCheckCircle,
  faTimesCircle,
  faPercent,
  faCalendarAlt,
  faInfoCircle,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "../../css/Common.css";
import "./css/PromotionsAdmin.css";
import APIService from "../../services/APIService";
import LoadingSpinner from "../../common/LoadingSpinner";
import toast from "react-hot-toast";
import { useTranslation } from "../../../../i18n/LanguageContext";

function PromotionsAdmin() {
  const { t } = useTranslation();
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPrograms, setExpandedPrograms] = useState({});

  // Load current discount programs and promo codes from the database
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [programsData, codesData] = await Promise.all([
        APIService.get("/api/v1/app/promotions/programs"),
        APIService.get("/api/v1/app/promotions/codes"),
      ]);
      const progs = Array.isArray(programsData) ? programsData : [];
      const codes = Array.isArray(codesData) ? codesData : [];
      setPrograms(progs);
      setPromoCodes(codes);
      // Auto-expand all programs that have codes
      const expanded = {};
      progs.forEach((p) => {
        if (codes.some((c) => c.discountProgramId === p.discountProgramId)) {
          expanded[p.discountProgramId] = true;
        }
      });
      setExpandedPrograms(expanded);
    } catch (error) {
      console.error("[PromotionsAdmin] Error loading promotions data:", error);
      setPrograms([]);
      setPromoCodes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Sync from Stripe
  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const result = await APIService.post(
        "/api/v1/app/promotions/sync-stripe",
      );
      setSyncResult(result);

      if (result.success) {
        const total =
          (result.couponsCreated || 0) +
          (result.couponsUpdated || 0) +
          (result.promoCodesCreated || 0) +
          (result.promoCodesUpdated || 0);
        toast.success(
          t("promotionsAdmin.toast.synced").replace("{total}", total),
        );
        await loadData();
      } else {
        toast.error(result.message || "Sync failed");
      }
    } catch (error) {
      console.error("[PromotionsAdmin] Sync error:", error);
      toast.error(
        "Failed to sync from Stripe: " + (error.message || "Unknown error"),
      );
    } finally {
      setSyncing(false);
    }
  };

  // Get promo codes for a specific program
  const getCodesForProgram = (programId) => {
    return promoCodes.filter((c) => c.discountProgramId === programId);
  };

  // Toggle expanded state for a program card
  const toggleProgram = (programId) => {
    setExpandedPrograms((prev) => ({
      ...prev,
      [programId]: !prev[programId],
    }));
  };

  // Format discount display
  const formatDiscount = (program) => {
    if (program.discountPercent > 0) return `${program.discountPercent}% off`;
    return "Fixed amount";
  };

  // Format duration display
  const formatDuration = (program) => {
    if (program.discountDuration === "forever") return "Forever";
    if (program.discountDuration === "once") return "Once";
    return `${program.durationInMonths || "?"} months`;
  };

  // Orphaned promo codes (no matching program)
  const orphanedCodes = promoCodes.filter(
    (c) => !programs.some((p) => p.discountProgramId === c.discountProgramId),
  );

  if (loading) {
    return (
      <div className="promotions-admin">
        <LoadingSpinner message="Loading promotions data..." />
      </div>
    );
  }

  return (
    <div className="promotions-admin">
      {/* ── Page Header ── */}
      <div className="promotions-page-header">
        <div className="promotions-header-info">
          <h2>
            <FontAwesomeIcon icon={faTag} className="header-icon" />
            Promotions & Coupons
          </h2>
          <p>Manage discount programs and promotion codes synced from Stripe</p>
        </div>
        <button
          className="sync-stripe-btn"
          onClick={handleSync}
          disabled={syncing}
        >
          <FontAwesomeIcon icon={faSync} spin={syncing} />
          {syncing ? "Syncing..." : "Sync from Stripe"}
        </button>
      </div>

      {/* ── Sync Result Banner ── */}
      {syncResult && (
        <div
          className={`sync-result-banner ${syncResult.success ? "success" : "error"}`}
        >
          <FontAwesomeIcon
            icon={syncResult.success ? faCheckCircle : faTimesCircle}
          />
          <div className="sync-result-details">
            {syncResult.success ? (
              <>
                <strong>Sync Complete:</strong> {syncResult.couponsCreated || 0}{" "}
                programs created, {syncResult.couponsUpdated || 0} updated |{" "}
                {syncResult.promoCodesCreated || 0} codes created,{" "}
                {syncResult.promoCodesUpdated || 0} updated
                {syncResult.errors?.length > 0 && (
                  <span className="sync-errors">
                    {" "}
                    | {syncResult.errors.length} error(s)
                  </span>
                )}
              </>
            ) : (
              <span>{syncResult.message || "Sync failed"}</span>
            )}
          </div>
          <button className="dismiss-btn" onClick={() => setSyncResult(null)}>
            &times;
          </button>
        </div>
      )}

      {/* ══════════ Discount Programs Section ══════════ */}
      <div className="section-card">
        <div className="section-card-header">
          <div className="section-title-row">
            <FontAwesomeIcon icon={faPercent} className="section-icon" />
            <h3>Discount Programs</h3>
            <span className="section-count">{programs.length}</span>
          </div>
          <span className="section-subtitle">
            {promoCodes.length} promotion code
            {promoCodes.length !== 1 ? "s" : ""} total
          </span>
        </div>

        <div className="section-card-body">
          {programs.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faInfoCircle} />
              <p>
                No discount programs found. Click "Sync from Stripe" to import
                coupons and promo codes.
              </p>
            </div>
          ) : (
            <div className="program-cards">
              {programs.map((program) => {
                const codes = getCodesForProgram(program.discountProgramId);
                const isExpanded = expandedPrograms[program.discountProgramId];

                return (
                  <div
                    key={program.discountProgramId}
                    className={`program-card ${isExpanded ? "expanded" : ""} ${program.isActive ? "card-active" : "card-inactive"}`}
                  >
                    {/* ── Program Header (clickable) ── */}
                    <button
                      className="program-card-header"
                      onClick={() => toggleProgram(program.discountProgramId)}
                      aria-expanded={isExpanded}
                    >
                      <span className="toggle-icon">
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronDown : faChevronRight}
                        />
                      </span>

                      <div className="program-card-title">
                        <span className="program-name">
                          {program.displayName}
                        </span>
                        {program.description && (
                          <span className="program-desc">
                            Synced from Stripe coupon: {program.stripeCouponId}
                          </span>
                        )}
                      </div>

                      <div className="program-card-pills">
                        <span className="pill pill-discount">
                          {formatDiscount(program)}
                        </span>
                        <span className="pill pill-duration">
                          {formatDuration(program)}
                        </span>
                        <span className="pill pill-codes">
                          <FontAwesomeIcon icon={faTag} />
                          {codes.length} code{codes.length !== 1 ? "s" : ""}
                        </span>
                        <span className="pill pill-redemptions">
                          {program.totalRedemptions || 0}
                          {program.maxRedemptions
                            ? ` / ${program.maxRedemptions}`
                            : ""}{" "}
                          redeemed
                        </span>
                        <span
                          className={`pill pill-status ${program.isActive ? "active" : "inactive"}`}
                        >
                          {program.isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </div>
                    </button>

                    {/* ── Expanded Body ── */}
                    {isExpanded && (
                      <div className="program-card-body">
                        {/* Details card */}
                        <div className="details-panel">
                          <div className="details-panel-header">
                            Program Details
                          </div>
                          <div className="details-grid">
                            <div className="detail-cell">
                              <span className="detail-label">
                                STRIPE COUPON
                              </span>
                              <code>{program.stripeCouponId || "—"}</code>
                            </div>
                            <div className="detail-cell">
                              <span className="detail-label">DISCOUNT</span>
                              <span className="detail-value highlight">
                                {formatDiscount(program)}
                              </span>
                            </div>
                            <div className="detail-cell">
                              <span className="detail-label">DURATION</span>
                              <span className="detail-value">
                                {formatDuration(program)}
                              </span>
                            </div>
                            <div className="detail-cell">
                              <span className="detail-label">REDEMPTIONS</span>
                              <span className="detail-value">
                                {program.totalRedemptions || 0}
                                {program.maxRedemptions
                                  ? ` / ${program.maxRedemptions}`
                                  : ""}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Promo codes nested section */}
                        {codes.length === 0 ? (
                          <div className="empty-state nested">
                            <FontAwesomeIcon icon={faInfoCircle} />
                            <p>
                              No promotion codes linked to this program yet.
                            </p>
                          </div>
                        ) : (
                          <div className="codes-section">
                            <div className="codes-section-header">
                              <FontAwesomeIcon icon={faTag} />
                              <span>PROMOTION CODES</span>
                              <span className="codes-count-badge">
                                {codes.length}
                              </span>
                            </div>
                            <div className="codes-table-container">
                              <table className="codes-table">
                                <thead>
                                  <tr>
                                    <th>Code</th>
                                    <th>Stripe Promo ID</th>
                                    <th>Redeemed</th>
                                    <th>Valid Until</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {codes.map((code) => (
                                    <tr key={code.promotionCodeId}>
                                      <td>
                                        <code className="promo-code-value">
                                          {code.code}
                                        </code>
                                      </td>
                                      <td className="stripe-id-cell">
                                        <code>
                                          {code.stripePromotionCodeId || "—"}
                                        </code>
                                      </td>
                                      <td>
                                        {code.timesRedeemed || 0}
                                        {code.maxRedemptions
                                          ? ` / ${code.maxRedemptions}`
                                          : ""}
                                      </td>
                                      <td>
                                        {code.validTo ? (
                                          <>
                                            <FontAwesomeIcon
                                              icon={faCalendarAlt}
                                              className="date-icon"
                                            />{" "}
                                            {code.validTo}
                                          </>
                                        ) : (
                                          "No expiry"
                                        )}
                                      </td>
                                      <td>
                                        <span
                                          className={`pill pill-status ${code.isActive ? "active" : "inactive"}`}
                                        >
                                          {code.isActive
                                            ? "ACTIVE"
                                            : "INACTIVE"}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ══════════ Orphaned / Unlinked Codes Section ══════════ */}
      {orphanedCodes.length > 0 && (
        <div className="section-card section-card-warning">
          <div className="section-card-header">
            <div className="section-title-row">
              <FontAwesomeIcon icon={faTag} className="section-icon" />
              <h3>Unlinked Codes</h3>
              <span className="section-count">{orphanedCodes.length}</span>
            </div>
            <span className="section-subtitle">
              Codes with no matching discount program in the local database
            </span>
          </div>
          <div className="section-card-body">
            <div className="codes-table-container">
              <table className="codes-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Program ID</th>
                    <th>Stripe Promo ID</th>
                    <th>Redeemed</th>
                    <th>Valid Until</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orphanedCodes.map((code) => (
                    <tr key={code.promotionCodeId}>
                      <td>
                        <code className="promo-code-value">{code.code}</code>
                      </td>
                      <td>#{code.discountProgramId}</td>
                      <td className="stripe-id-cell">
                        <code>{code.stripePromotionCodeId || "—"}</code>
                      </td>
                      <td>
                        {code.timesRedeemed || 0}
                        {code.maxRedemptions ? ` / ${code.maxRedemptions}` : ""}
                      </td>
                      <td>
                        {code.validTo ? (
                          <>
                            <FontAwesomeIcon
                              icon={faCalendarAlt}
                              className="date-icon"
                            />{" "}
                            {code.validTo}
                          </>
                        ) : (
                          "No expiry"
                        )}
                      </td>
                      <td>
                        <span
                          className={`pill pill-status ${code.isActive ? "active" : "inactive"}`}
                        >
                          {code.isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromotionsAdmin;
